import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Button,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { getApi } from 'views/services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import BoltIcon from '@mui/icons-material/Bolt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const RoomInvoice = () => {
  const theme = useTheme();
  const [invoiceData, setInvoiceData] = useState({});
  const [itemsData, setitemsData] = useState([]);
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';
  const params = useParams();
  const _id = params.id;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        const response = await getApi(`api/invoice/viewspecificinvoice/${_id}`);
        setInvoiceData(response?.data?.InvoiceData);
      } catch (error) {
        console.log(error);
      }
    };
    fetchInvoiceData();
  }, [_id]);

  const handlePrint = () => {
    window.print();
  };

  const fetchitemsData = async () => {
    try {
      const response = await getApi(`api/reservation/getfooditems/${invoiceData.reservationId}`);
      setitemsData(response?.data?.foodItemsData[0]?.foodItems);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (invoiceData.type === 'food') {
      fetchitemsData();
    }
  }, [invoiceData.type]);

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Contado / Efectivo',
      creditCard: 'Tarjeta de Crédito',
      debitCard: 'Tarjeta de Débito',
      netBanking: 'Transferencia Bancaria',
      onlinePayment: 'Pago Móvil / En Línea'
    };
    return methods[method] || method;
  };

  // Calculations for display
  const totalAmount = invoiceData?.totalAmount || 0;
  const gstAmount = invoiceData?.gstAmount || 0;
  const baseImponible = invoiceData?.haveGST ? (totalAmount - gstAmount) : totalAmount;
  const exento = 0; // Adjustable if you add exempt items later
  const ivaPercentage = invoiceData?.gstPercentage || 16;
  const controlNumber = `00-${invoiceData?.invoiceNumber?.split('-').pop() || '000000'}`;

  const formattedDate = invoiceData?.createdDate
    ? new Date(invoiceData.createdDate).toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' });

  const formattedTime = invoiceData?.createdDate
    ? new Date(invoiceData.createdDate).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })
    : new Date().toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f6f6f8', py: { xs: 2, md: 5 }, px: { xs: 2, md: 5 } }}>
      <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
        {/* Top Navigation Bar / Actions */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            borderRadius: '12px 12px 0 0',
            px: 3,
            py: 2,
            mb: 0
          }}
          className="print-hide"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
              <ReceiptLongIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>Factura Digital</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>Providencia SENIAT 0071</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ height: 40, borderRadius: 2, color: 'text.secondary', borderColor: 'divider', textTransform: 'none', fontWeight: 700 }}>
              Regresar
            </Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ height: 40, borderRadius: 2, boxShadow: 'none', textTransform: 'none', fontWeight: 700 }}>
              Imprimir
            </Button>
          </Stack>
        </Paper>

        {/* Invoice Body */}
        <Paper elevation={3} sx={{ borderRadius: '0 0 12px 12px', border: 1, borderColor: 'divider', borderTop: 0, p: { xs: 3, md: 5 }, bgcolor: 'white' }}>

          {/* Invoice Header: Branding and Legal Numbers */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 4, mb: 4, pb: 4, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                {hotel?.hotelImage ? (
                  <Box component="img" src={hotel.hotelImage} alt="Hotel Logo" sx={{ maxHeight: 48, maxWidth: 120, objectFit: 'contain' }} />
                ) : (
                  <Box sx={{ width: 48, height: 48, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                    <BoltIcon fontSize="large" />
                  </Box>
                )}
                <Typography variant="h5" fontWeight={900} textTransform="uppercase" letterSpacing="-0.5px">
                  {hotel?.name || 'Inversiones Tech C.A.'}
                </Typography>
              </Stack>
              <Stack spacing={0.5} sx={{ typography: 'body2', color: 'text.secondary' }}>
                <Typography variant="body2" fontWeight={700} color="text.primary">RIF: {hotel?.gstNumber || 'J-XXXXXXXX-X'}</Typography>
                <Typography variant="body2">{hotel?.address || 'Dirección de la empresa'}</Typography>
                <Typography variant="body2">Teléfono: {hotel?.contact || 'N/A'}</Typography>
                <Typography variant="body2">Email: {hotel?.email || 'N/A'}</Typography>
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', textAlign: 'right' }}>
              <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), border: 1, borderColor: alpha(theme.palette.primary.main, 0.2), p: 2, borderRadius: 3, display: 'inline-block' }}>
                <Typography variant="h4" fontWeight={900} color="primary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '2px' }}>FACTURA</Typography>
                <Stack spacing={0.5}>
                  <Typography variant="h6" fontWeight={700} fontFamily="monospace">N° {invoiceData?.invoiceNumber?.split('-').pop() || '000000'}</Typography>
                  <Box sx={{ height: '1px', bgcolor: alpha(theme.palette.primary.main, 0.2), my: 1 }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>N° DE CONTROL</Typography>
                  <Typography variant="body1" fontWeight={700} fontFamily="monospace" color="error.main">{controlNumber}</Typography>
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Recipient Information */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: 'grey.50', p: 2.5, borderRadius: 3, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="overline" fontWeight={900} color="primary" sx={{ display: 'block', mb: 1.5, letterSpacing: '1px' }}>DATOS DEL CLIENTE</Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Nombre o Razón Social:</Typography>
                    <Typography variant="body2" fontWeight={700} textTransform="uppercase">{invoiceData?.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">RIF / C.I. / Pasaporte:</Typography>
                    <Typography variant="body2" fontWeight={600}>{invoiceData?.gstNumber || 'No especificado'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Dirección Fiscal:</Typography>
                    <Typography variant="body2" fontWeight={600}>{invoiceData?.address || 'No especificada'}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: 'grey.50', p: 2.5, borderRadius: 3, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="overline" fontWeight={900} color="primary" sx={{ display: 'block', mb: 1.5, letterSpacing: '1px' }}>DETALLES DE EMISIÓN</Typography>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Fecha de Emisión:</Typography>
                      <Typography variant="body2" fontWeight={700} textTransform="uppercase">{formattedDate}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Hora:</Typography>
                      <Typography variant="body2" fontWeight={700} textTransform="uppercase">{formattedTime}</Typography>
                    </Grid>
                  </Grid>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Condición de Pago:</Typography>
                    <Typography variant="body2" fontWeight={700} textTransform="uppercase">{getPaymentMethodLabel(invoiceData?.paymentMethod)}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {/* Items Table */}
          <Box sx={{ mb: 4, overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
            <TableContainer sx={{ border: 'none' }}>
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#0f172a' }}>
                    <TableCell sx={{ color: 'white', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', borderBottom: 'none' }}>Item</TableCell>
                    <TableCell sx={{ color: 'white', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', borderBottom: 'none' }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ color: 'white', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', borderBottom: 'none' }}>Cant.</TableCell>
                    <TableCell align="right" sx={{ color: 'white', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', borderBottom: 'none' }}>P. Unitario ({currency})</TableCell>
                    <TableCell align="right" sx={{ color: 'white', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', borderBottom: 'none' }}>Total ({currency})</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ '& .MuiTableCell-root': { py: 2, borderBottom: '1px solid', borderColor: 'grey.100' } }}>

                  {invoiceData?.roomRent > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>01</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Servicio de Alojamiento</TableCell>
                      <TableCell align="center">1</TableCell>
                      <TableCell align="right">{invoiceData.roomRent.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{invoiceData.roomRent.toFixed(2)}</TableCell>
                    </TableRow>
                  )}

                  {invoiceData.type === 'food' && itemsData?.map((foodItem, index) => (
                    <TableRow key={foodItem._id}>
                      <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{(invoiceData?.roomRent ? index + 2 : index + 1).toString().padStart(2, '0')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{foodItem.name}</TableCell>
                      <TableCell align="center">{foodItem.quantity}</TableCell>
                      <TableCell align="right">{foodItem.price.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{foodItem.totalAmountFood.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}

                  {/* Adelantos o descuentos restan de este bloque */}
                  {invoiceData?.discount > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>DESC</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'warning.main' }}>Descuento Aplicado</TableCell>
                      <TableCell align="center">-</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>-{invoiceData.discount.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'warning.main' }}>-{invoiceData.discount.toFixed(2)}</TableCell>
                    </TableRow>
                  )}
                  {invoiceData?.advanceAmount > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>ADEL</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>Adelanto Cobrado Anteriormente</TableCell>
                      <TableCell align="center">-</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>-{invoiceData.advanceAmount.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>-{invoiceData.advanceAmount.toFixed(2)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Footer Totals */}
          <Grid container spacing={4} sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2, border: 1, borderColor: alpha(theme.palette.primary.main, 0.1) }}>
                <Typography variant="overline" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>Observaciones:</Typography>
                <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block">
                  Esta factura cumple con la Providencia Administrativa N° 0071 del SENIAT. Los pagos realizados mediante transferencia bancaria deben ser reportados al departamento de cobranzas o administración y tener los soportes correspondientes.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5} sx={{ pl: { md: 4 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>Sub-Total (Base Imponible):</Typography>
                  <Typography variant="body2" fontWeight={700}>{currency} {baseImponible.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>Exento / No Sujeto:</Typography>
                  <Typography variant="body2" fontWeight={700}>{currency} {exento.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>I.V.A ({ivaPercentage}%):</Typography>
                  <Typography variant="body2" fontWeight={700}>{currency} {gstAmount.toFixed(2)}</Typography>
                </Stack>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>I.V.A (8% - Reducido):</Typography>
                    <Typography variant="caption" fontWeight={700}>Bs. 0,00</Typography>
                  </Stack>
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                  <Typography variant="subtitle1" fontWeight={900} textTransform="uppercase" letterSpacing="-0.5px">Total a Pagar:</Typography>
                  <Typography variant="h5" fontWeight={900} color="primary">{currency} {totalAmount.toFixed(2)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          {/* Legal Printing Info */}
          <Box sx={{ mt: 8, pt: 4, borderTop: '2px dashed', borderColor: 'divider' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={0.5} sx={{ typography: 'caption', fontSize: '0.6rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <Typography variant="inherit">Imprenta Autorizada: Sistemas y Tecnologías Libres, C.A.</Typography>
                  <Typography variant="inherit">RIF: J-50284411-9 | Tel: (0212) 555-0000</Typography>
                  <Typography variant="inherit">Resolución SENIAT N°: 0045/2023 de Fecha 12/01/2023</Typography>
                  <Typography variant="inherit">Talonario del 000001 al 500000</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
                <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                  <Box sx={{ border: 1, borderColor: 'divider', px: 1, py: 0.5, borderRadius: 1 }}>
                    <Typography variant="caption" fontSize="0.6rem" fontWeight={700}>ORIGINAL: CLIENTE</Typography>
                  </Box>
                  <Box sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                    <Typography variant="caption" fontSize="0.6rem" fontWeight={700} color="text.secondary">COPIA: CONTABILIDAD</Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" fontSize="0.6rem" color="text.disabled" fontWeight={700} textTransform="uppercase" letterSpacing="0.5px">
                  © {new Date().getFullYear()} Factura Digital - Sistema Validado v2.4.1
                </Typography>
              </Grid>
            </Grid>
          </Box>

        </Paper>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }} className="print-hide">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'none' }}>
            Volver al Listado de Facturas
          </Button>
        </Box>
      </Box>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            body { background: white !important; }
            .print-hide { display: none !important; }
            @page { size: letter; margin: 10mm 15mm; }
          }
        `}
      </style>
    </Box>
  );
};

export default RoomInvoice;
